import {
  createNewItemInvoiceApi,
  createNewItemReturnApi,
  DeleteInvoiceItemAPI,
  DeleteReturnItemAPI,
  UpdateInvoiceItemApi,
  UpdateReturnItemApi,
} from "../ApiRequests";

/** Guest / customer display helpers for invoices (sales, ledger, reports). */
export const isGuestInvoice = (invoice) => {
  const type = invoice?.invoice_customer_type;
  if (type === "GUEST" || type === "WALK_IN") return true;
  if (!invoice?.customerId && (invoice?.guest_name || invoice?.walk_in_name)) {
    return true;
  }
  return false;
};

export const getInvoicePartyName = (invoice) => {
  if (!invoice) return "";

  const guestName = invoice.guest_name || invoice.walk_in_name;
  if (guestName) return guestName;

  if (isGuestInvoice(invoice)) return "Guest";

  const customer = invoice.customerId;
  if (typeof customer === "object" && customer?.name) {
    return customer.name;
  }
  if (typeof customer === "string" && invoice.customer_name) {
    return invoice.customer_name;
  }
  return customer?.name || invoice.customer_name || "";
};

export const getInvoicePartyContact = (invoice) => {
  if (!invoice) return "";
  const guestContact = invoice.guest_contact || invoice.walk_in_contact;
  if (guestContact) return guestContact;
  if (isGuestInvoice(invoice)) return "";
  return invoice.customerId?.contact || "";
};

export const getInvoicePartyAddress = (invoice) => {
  if (!invoice) return "";
  const guestAddress = invoice.guest_address || invoice.walk_in_address;
  if (guestAddress) return guestAddress;
  if (isGuestInvoice(invoice)) return "";
  return invoice.customerId?.address || "";
};

/** Ledger "Shopping Wala" person — stored on customer.ref */
export const getInvoiceShoppingWala = (invoice) => {
  if (!invoice || isGuestInvoice(invoice)) return "";
  const customer = invoice.customerId;
  if (typeof customer === "object" && customer) {
    return String(customer.ref ?? "").trim();
  }
  return String(invoice.ref ?? "").trim();
};

const productItemId = (product) =>
  String(product?.itemId?._id || product?.itemId || "");

const editLineChanged = (row, original) => {
  if (!original) return true;
  return (
    productItemId(original) !== String(row.itemId) ||
    Number(original.qty) !== Number(row.qty) ||
    Number(original.price) !== Number(row.price) ||
    Number(original.amount) !== Number(row.amount) ||
    Number(original.purchase || 0) !== Number(row.purchase || 0) ||
    String(original.unit || "") !== String(row.unit || "") ||
    String(original.name || "") !== String(row.name || "")
  );
};

/**
 * Validate sale qty against stock, crediting qty already on this invoice's lines.
 * Returns an error message or null.
 */
export const validateEditSaleStock = (
  committedRows,
  originalProducts = [],
  stockList = []
) => {
  const creditedByItem = {};
  for (const product of originalProducts || []) {
    const id = productItemId(product);
    if (!id) continue;
    creditedByItem[id] =
      (creditedByItem[id] || 0) + (Number(product.qty) || 0);
  }

  const neededByItem = {};
  for (const row of committedRows || []) {
    const id = String(row.itemId);
    neededByItem[id] = (neededByItem[id] || 0) + (Number(row.qty) || 0);
  }

  for (const [itemId, needed] of Object.entries(neededByItem)) {
    const stock = (stockList || []).find(
      (item) => String(item._id) === String(itemId)
    );
    if (!stock) continue;
    const available =
      Number(stock.qty || 0) + (creditedByItem[itemId] || 0);
    if (needed > available) {
      return `Quantity must be ≤ ${available} for ${stock.name}`;
    }
  }
  return null;
};

/**
 * Apply local edit-page item diffs via existing item APIs (qty / account side effects),
 * then caller should update header details (customer / discount / payment).
 * Always pass the document's current (DB) customerId for accountCustomerId so
 * customer-change reverse/apply stays correct in the details endpoint.
 */
export const syncEditDocumentItems = async ({
  documentId,
  isReturn = false,
  accountCustomerId,
  originalItems = [],
  committedRows = [],
}) => {
  const origById = new Map(
    (originalItems || []).map((product) => [String(product._id), product])
  );
  const keptIds = new Set(
    (committedRows || [])
      .filter((row) => row.productLineId)
      .map((row) => String(row.productLineId))
  );

  for (const id of origById.keys()) {
    if (keptIds.has(id)) continue;
    if (isReturn) await DeleteReturnItemAPI(id);
    else await DeleteInvoiceItemAPI(id);
  }

  for (const row of committedRows || []) {
    const linePayload = {
      itemId: row.itemId,
      name: row.name,
      qty: Number(row.qty),
      unit: row.unit,
      price: Number(row.price),
      purchase: Number(row.purchase || row.price),
      amount: Number(row.amount),
    };

    if (row.productLineId && origById.has(String(row.productLineId))) {
      const original = origById.get(String(row.productLineId));
      if (!editLineChanged(row, original)) continue;
      if (isReturn) {
        await UpdateReturnItemApi(row.productLineId, {
          return_id: documentId,
          ...linePayload,
        });
      } else {
        await UpdateInvoiceItemApi(row.productLineId, {
          invoice_id: documentId,
          ...linePayload,
        });
      }
      continue;
    }

    if (isReturn) {
      await createNewItemReturnApi({
        ...(accountCustomerId ? { customerId: accountCustomerId } : {}),
        returnid: documentId,
        ...linePayload,
      });
    } else {
      await createNewItemInvoiceApi({
        ...(accountCustomerId ? { customerId: accountCustomerId } : {}),
        invoiceid: documentId,
        ...linePayload,
      });
    }
  }
};
